import React from "react";
import { Form, type FormControlProps } from "banco-components";
import { Controller, useFormContext } from "react-hook-form";
import { default as ReactCurrencyInput } from "react-currency-input-field";
import { get } from "lodash";
import { FormControllerWrapper, type FormControllerWrapperProps } from "./FormControllerWrapper";

interface CurrencyInputProps extends FormControllerWrapperProps {
  value?: string
  allowDecimal?: boolean
  onChangeHandler?: (newVal: string | undefined) => void;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> & FormControlProps & {
    [key: `data-${string}`]: string
  }
  afterOnChange?: (newVal: string | undefined) => void
}

/**
 * Used within a form for capturing currency inputs such as income or expenses
 * It wil store the value without the formatting (such as commas)
 *
 * @example
 * <CurrencyInput fieldName="income" />
 *
 * @param value - The value of the field
 * @param allowDecimal - Whether to allow decimal values (default is true)
 * @param prependSymbol - The symbol to prepend to the input (default is £)
 * @param onChangeHandler - Custom handler for checkbox change (does not update form value)
 * @param afterOnChange - Custom handler to fire after the form value changes
 * @param inputProps - Additional props to pass to the input
 * @param controllerProps - Additional props to pass to the controller
 */
export const CurrencyInput = ({
  value, allowDecimal = false, prependSymbol = "£", inputProps,
  onChangeHandler, afterOnChange, controllerProps, inputRef, ...rest
}: CurrencyInputProps) => {
  const formMethods = useFormContext();

  const err = get(formMethods.formState.errors, rest.fieldName);

  // Add a flag to track if the input has been clicked before
  const [isFirstClick, setIsFirstClick] = React.useState<boolean>(true);

  return (
    <FormControllerWrapper { ...rest } prependSymbol={ prependSymbol } showFeedback>
      <Controller
        name={ rest.fieldName }
        control={ formMethods.control }
        { ...controllerProps }
        render={ ({ field }) => (
          <Form.Control
            as={ ReactCurrencyInput }
            { ...field }
            id={ rest.fieldName }
            data-bind-value={ rest.fieldName }
            data-fb-input={ rest.fieldName }
            { ...inputProps }
            ref={ inputRef }
            allowDecimals={ allowDecimal }
            decimalScale={ allowDecimal ? 2 : 0 } // 1.2, will be converted to 1.20
            allowNegativeValue={ false }
            value={ value || field.value }
            onChange={ undefined }
            onValueChange={ (val: string | undefined) => {
              if (onChangeHandler) {
                onChangeHandler(val);
              } else {
                field.onChange(val || "");
                formMethods.trigger(rest.fieldName);
                afterOnChange && afterOnChange(val);
              }
            } }
            onClick={ (e: React.MouseEvent<HTMLInputElement>) => {
              if (isFirstClick) {
                e.currentTarget.select();
                e.currentTarget.setSelectionRange(0, e.currentTarget.value.length);
                setIsFirstClick(false);
              }
            } }
            // Add onBlur handler to reset the isFirstClick state
            onBlur={ () => {
              setIsFirstClick(true);
              field.onBlur();
            } }
            isInvalid={ Boolean(err) }
            onWheel={ (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur() }
            intlConfig={ { locale: "en-GB" } } /* DO NOT REMOVE, Force en-GB locale */
          />
        ) }
      />
    </FormControllerWrapper>
  );
};
